"use strict";

import {onFind} from "@elements/init-modules-in-scope";
import {findIn, closest, removeClass, findAllIn, addClass, hasClass, on, off} from '@elements/dom-utils';

const selectors = {
    base: '.js-keyboard-nav',
    list: '.js-nav__list',
    listItem: '.js-nav__list-item',
    listToggle: '.js-nav__list-toggle',
    item: 'a, button, [tabindex]',
    overlay: '.js-nav__overlay'
};

export function init() {
    onFind(selectors.base, function (baseElement) {
        const lists = findAllIn(selectors.list, baseElement);

        lists.forEach(function(list) {
            const listItems = findAllIn(selectors.listItem, list);
            const parentItem = closest(selectors.listItem, list) || baseElement;

            // Initialize keyboard navigation for this list
            initKeyboardNavigation(parentItem, list);
        });
    });
}

function initKeyboardNavigation(parentItem, list) {
    const toggle = findIn(selectors.listToggle, parentItem);

    const handleArrowNavigation = (evt, direction) => {
        evt.preventDefault();
        const target = evt.target;
        const parent = closest(selectors.listItem, target);

        if (parent) {
            const sibling = direction === 'previous'
                ? parent.previousElementSibling
                : parent.nextElementSibling;

            if (sibling) {
                const link = findIn(selectors.item, sibling);
                if (link) {
                    link.focus();
                }
            }
        }
    };

    const keydownHandler = (event) => {
        switch (event.code) {
            case 'ArrowDown':
                handleArrowNavigation(event, 'next');
                break;
            case 'ArrowUp':
                handleArrowNavigation(event, 'previous');
                break;
            case 'Escape':
                handleEscapeKey(parentItem, toggle);
                break;
            case 'Tab':
                handleTabKey(event, parentItem);
                break;
        }
    };

    const focusoutHandler = (evt) => {
        const target = evt.relatedTarget;
        const toggleClass = selectors.listToggle.replace('.', '');

        if (!parentItem.contains(target) || (target && hasClass(toggleClass, target))) {
            closeNavigation(parentItem);
        }
    };

    const focusinHandler = (evt) => {
        const target = evt.target;
        if (parentItem.contains(target)) {
            const firstLink = findIn(selectors.item, list);
            if (firstLink && !hasClass('is-open', parentItem)) {
                openNavigation(parentItem);
            }
        }
    };

    // Add event listeners
    addEventListeners(parentItem, list, keydownHandler, focusoutHandler, focusinHandler);
}

function handleEscapeKey(parentItem, toggle) {
    closeNavigation(parentItem);
    if (toggle) {
        toggle.focus();
    }
}

function handleTabKey(event, parentItem) {
    // Allow natural tab navigation but close menu if tabbing out
    const target = event.target;
    if (!parentItem.contains(target)) {
        closeNavigation(parentItem);
    }
}

function openNavigation(parentItem) {
    addClass('is-open', parentItem);
    const list = findIn(selectors.list, parentItem);
    if (list) {
        const firstLink = findIn(selectors.item, list);
        if (firstLink) {
            firstLink.focus();
        }
    }
}

function closeNavigation(parentItem) {
    removeClass('is-open', parentItem);
    cleanupListeners(parentItem);
}

function addEventListeners(parentItem, list, keydownHandler, focusoutHandler, focusinHandler) {
    on('keydown', keydownHandler, parentItem);
    on('focusout', focusoutHandler, list);
    on('focusin', focusinHandler, parentItem);

    // Store handlers for cleanup
    parentItem._keyboardNavHandlers = {
        keydown: keydownHandler,
        focusout: focusoutHandler,
        focusin: focusinHandler
    };
}

function cleanupListeners(parentItem) {
    const handlers = parentItem._keyboardNavHandlers;
    if (handlers) {
        const list = findIn(selectors.list, parentItem);

        off('keydown', handlers.keydown, parentItem);
        off('focusout', handlers.focusout, list);
        off('focusin', handlers.focusin, parentItem);

        delete parentItem._keyboardNavHandlers;
    }
}

// Helper function to focus first item in navigation
export function focusFirstNavItem(navElement) {
    const firstItem = findIn(selectors.item, navElement);
    if (firstItem) {
        firstItem.focus();
    }
}

// Helper function to manually trigger keyboard navigation on an element
export function enableKeyboardNav(element) {
    const list = findIn(selectors.list, element);
    if (list) {
        initKeyboardNavigation(element, list);
    }
}